import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { WorkerProfileField } from 'types/profileCustomFields';
import { IContactFormState } from 'modules/views/pages/ContactInformation/types';
import { shouldShowStateForCountry } from 'utils/addressValidationUtils';

/**
 * Exported field configuration for Contact Information
 * Used across the app for consistent field mapping
 */
export const CONTACT_INFO_FIELD_CONFIG: Partial<
  Record<
    WorkerProfileField,
    {
      displayNameKey: string;
      formField: string;
      reduxField?: string;
      modalDisplayNameKey?: string;
      shouldCheck?: (onboardingState: any) => boolean;
    }
  >
> = {
  // Contact Information fields
  [WorkerProfileField.alternateContactMobilePhoneNumber]: {
    displayNameKey: 'contactInformation.contactInfo.alternatePhone',
    formField: 'phoneNumber',
    reduxField: 'phoneNumber', // Redux state path
  },

  // Home Address fields
  [WorkerProfileField.homeAddressLine1]: {
    displayNameKey: 'contactInformation.homeAddress.addressLine1',
    formField: 'addressLine1',
    reduxField: 'address.line1', // Redux state path
  },
  [WorkerProfileField.homeAddressLine2]: {
    displayNameKey: 'contactInformation.homeAddress.addressLine2',
    formField: 'addressLine2',
    reduxField: 'address.line2', // Redux state path
  },
  [WorkerProfileField.homeAddressCity]: {
    displayNameKey: 'contactInformation.homeAddress.city',
    formField: 'city',
    reduxField: 'address.city', // Redux state path
  },
  [WorkerProfileField.homeAddressZipPostal]: {
    displayNameKey: 'contactInformation.homeAddress.zipCode',
    formField: 'zipCode',
    reduxField: 'address.zipCode', // Redux state path
  },
  [WorkerProfileField.homeAddressCountry]: {
    displayNameKey: 'contactInformation.homeAddress.country',
    formField: 'country',
    reduxField: 'address.country.id', // Redux state path
  },
  [WorkerProfileField.homeAddressStateProvince]: {
    displayNameKey: 'contactInformation.homeAddress.state',
    formField: 'state',
    reduxField: 'address.stateCode', // Redux state path
    // Only check this field if country supports states (US, CA, MX)
    // Uses centralized logic from addressValidationUtils
    // Accepts onboardingState and full Redux state for flexible data access
    shouldCheck: (onboardingState: any, reduxState?: any) => {
      // Try to get country code directly from onboarding state first
      let countryCode = onboardingState?.address?.country?.code;

      // If not available, look it up from cache using country ID
      if (!countryCode && reduxState) {
        const countryId = onboardingState?.address?.country?.id;
        const language = reduxState?.app?.languageCode || 'en';
        const countries = reduxState?.cache?.countries;

        if (countries && countryId) {
          const currentLanguageCountries = countries[language] || [];
          const country = currentLanguageCountries.find((c: any) => c.id === countryId);
          countryCode = country?.code;
        }
      }

      return shouldShowStateForCountry(countryCode);
    },
  },

  // Emergency Contact fields - only add modalDisplayNameKey where we need override
  [WorkerProfileField.emergencyContactName]: {
    displayNameKey: 'contactInformation.emergencyContact.name',
    modalDisplayNameKey: 'contactInformation.emergencyContact.modalName', // Emergency Name
    formField: 'emergencyContactName',
    reduxField: 'emergencyContactName', // Redux state path
  },
  [WorkerProfileField.emergencyContactMobilePhone]: {
    displayNameKey: 'contactInformation.emergencyContact.mobileNumber',
    modalDisplayNameKey: 'contactInformation.emergencyContact.modalMobileNumber', // Emergency Mobile Number
    formField: 'emergencyContactNumber',
    reduxField: 'emergencyContactPhone', // Redux state path
  },
  [WorkerProfileField.emergencyContactRelationship]: {
    displayNameKey: 'contactInformation.emergencyContact.relationship',
    modalDisplayNameKey: 'contactInformation.emergencyContact.modalRelationship', // Emergency Relationship
    formField: 'emergencyContactRelationship',
    reduxField: 'emergencyContactRelationship', // Redux state path
  },
};

// Static label keys - no translations here
const LABEL_KEYS = {
  // Contact Info
  email: 'contactInformation.contactInfo.email',
  mobileNumber: 'contactInformation.contactInfo.mobileNumber',
  alternatePhone: 'contactInformation.contactInfo.alternatePhone',

  // Home Address
  addressLine1: 'contactInformation.homeAddress.addressLine1',
  addressLine2: 'contactInformation.homeAddress.addressLine2',
  city: 'contactInformation.homeAddress.city',
  zipCode: 'contactInformation.homeAddress.zipCode',
  country: 'contactInformation.homeAddress.country',
  state: 'contactInformation.homeAddress.state',
  county: 'contactInformation.homeAddress.county',
  borough: 'contactInformation.homeAddress.borough',

  // Emergency Contact
  emergencyContactName: 'contactInformation.emergencyContact.name',
  emergencyContactRelationship: 'contactInformation.emergencyContact.relationship',
  emergencyContactMobileNumber: 'contactInformation.emergencyContact.mobileNumber',

  // Section Titles
  contactInfoTitle: 'contactInformation.contactInfo.title',
  homeAddressTitle: 'contactInformation.homeAddress.title',
  emergencyContactTitle: 'contactInformation.emergencyContact.title',
} as const;

// Static WorkerProfileField mappings
const WORKER_PROFILE_FIELDS = {
  email: WorkerProfileField.email,
  mobilePhoneNumber: WorkerProfileField.mobilePhoneNumber,
  alternateContactMobilePhoneNumber: WorkerProfileField.alternateContactMobilePhoneNumber,
  homeAddressLine1: WorkerProfileField.homeAddressLine1,
  homeAddressLine2: WorkerProfileField.homeAddressLine2,
  homeAddressCity: WorkerProfileField.homeAddressCity,
  homeAddressZipPostal: WorkerProfileField.homeAddressZipPostal,
  homeAddressCountry: WorkerProfileField.homeAddressCountry,
  homeAddressStateProvince: WorkerProfileField.homeAddressStateProvince,
  emergencyContactName: WorkerProfileField.emergencyContactName,
  emergencyContactMobilePhone: WorkerProfileField.emergencyContactMobilePhone,
  emergencyContactRelationship: WorkerProfileField.emergencyContactRelationship,
} as const;

/**
 * Custom hook for managing ContactInformation field configuration
 * Single source of truth with optimized translation handling
 */
export const useContactInformationFields = () => {
  const { t } = useTranslation();

  // Get field display name (translated on-demand)
  const getFieldDisplayName = useCallback(
    (field: WorkerProfileField): string => {
      const config = CONTACT_INFO_FIELD_CONFIG[field as keyof typeof CONTACT_INFO_FIELD_CONFIG];
      return config ? t(config.displayNameKey) : field.toString();
    },
    [t],
  );

  // Get field display name for modal (translated on-demand with fallback to default)
  const getModalFieldDisplayName = useCallback(
    (field: WorkerProfileField): string => {
      const config = CONTACT_INFO_FIELD_CONFIG[field as keyof typeof CONTACT_INFO_FIELD_CONFIG];
      if (!config) return field.toString();

      // Use modalDisplayNameKey if available, otherwise fall back to displayNameKey
      const keyToUse = 'modalDisplayNameKey' in config ? config.modalDisplayNameKey : config.displayNameKey;
      return t(keyToUse);
    },
    [t],
  );

  // Form field mapping - derived from static config
  const formFieldMap: Partial<Record<WorkerProfileField, string>> = useMemo(() => {
    return Object.entries(CONTACT_INFO_FIELD_CONFIG).reduce(
      (acc, [field, config]) => {
        acc[field as WorkerProfileField] = config.formField;
        return acc;
      },
      {} as Partial<Record<WorkerProfileField, string>>,
    );
  }, []);

  // Field display names for modal - computed on-demand using modal-specific translations
  const fieldDisplayNames: Partial<Record<WorkerProfileField, string>> = useMemo(() => {
    return Object.keys(formFieldMap).reduce(
      (acc, fieldKey) => {
        const field = fieldKey as WorkerProfileField;
        acc[field] = getModalFieldDisplayName(field);
        return acc;
      },
      {} as Partial<Record<WorkerProfileField, string>>,
    );
  }, [getModalFieldDisplayName, formFieldMap]); // Helper function to get dynamic form field map
  const getDynamicFormFieldMap = useCallback(
    (formValues?: IContactFormState): Partial<Record<WorkerProfileField, string>> => {
      return { ...formFieldMap };
    },
    [formFieldMap],
  );

  // Centralized labels - all translations in one place
  const labels = useMemo(
    () => ({
      email: t(LABEL_KEYS.email),
      mobileNumber: t(LABEL_KEYS.mobileNumber),
      alternatePhone: t(LABEL_KEYS.alternatePhone),
      addressLine1: t(LABEL_KEYS.addressLine1),
      addressLine2: t(LABEL_KEYS.addressLine2),
      city: t(LABEL_KEYS.city),
      zipCode: t(LABEL_KEYS.zipCode),
      country: t(LABEL_KEYS.country),
      state: t(LABEL_KEYS.state),
      county: t(LABEL_KEYS.county),
      borough: t(LABEL_KEYS.borough),
      emergencyContactName: t(LABEL_KEYS.emergencyContactName),
      emergencyContactRelationship: t(LABEL_KEYS.emergencyContactRelationship),
      emergencyContactMobileNumber: t(LABEL_KEYS.emergencyContactMobileNumber),
      contactInfoTitle: t(LABEL_KEYS.contactInfoTitle),
      homeAddressTitle: t(LABEL_KEYS.homeAddressTitle),
      emergencyContactTitle: t(LABEL_KEYS.emergencyContactTitle),
    }),
    [t],
  );

  return {
    // Keep existing API for backward compatibility
    fieldConfig: CONTACT_INFO_FIELD_CONFIG,
    getFieldDisplayName,
    formFieldMap,
    fieldDisplayNames,
    getDynamicFormFieldMap,
    labels,
    workerProfileFields: WORKER_PROFILE_FIELDS,
  };
};
